import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Trash } from '@phosphor-icons/react';
import { Button } from '@/components/ui-kit/button';
import { Textarea } from '@/components/ui-kit/textarea';
import { Label } from '@/components/ui-kit/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui-kit/select';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui-kit/form';
import { useToast } from '@/hooks/use-toast';
import { translatedZodResolver } from '@/lib/utils/validation/translated-zod-resolver';
import {
  useCreateConditionalOffer,
  useGetDepartmentsLookup,
  useGetEmployeesLookup,
} from '../../hooks/use-offers';
import { offerFormSchema, OfferFormValues } from '../../schemas/offer-form-schema';
import { DepartmentOption, EmployeesLookupResult } from '../../types/offer.types';
import { candidateFormDefaults } from '../../../shared/candidate-form-schema';
import { CandidateFields } from '../../../shared/components/candidate-fields';

export const OfferForm = () => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [panel, setPanel] = useState<{ name: string; id: string }[]>([]);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState('');

  const { data: departmentsData } = useGetDepartmentsLookup();
  const departments =
    (departmentsData as { Departments?: { items: DepartmentOption[] } })?.Departments?.items ?? [];

  const { data: employeesData } = useGetEmployeesLookup('');
  const employees =
    (employeesData as { Employees?: EmployeesLookupResult })?.Employees?.items ?? [];

  const { mutateAsync: createConditionalOffer, isPending } = useCreateConditionalOffer();

  const form = useForm<OfferFormValues>({
    resolver: translatedZodResolver(offerFormSchema, t),
    defaultValues: { ...candidateFormDefaults, HiringDepartmentId: '' },
  });

  const addPanelMember = () => {
    const employee = employees.find((option) => option.ItemId === selectedEmployeeId);
    if (!employee || panel.some((member) => member.id === employee.ItemId)) return;
    setPanel([...panel, { name: employee.DisplayName, id: employee.ItemId }]);
    setSelectedEmployeeId('');
  };

  const removePanelMember = (id: string) => {
    setPanel(panel.filter((member) => member.id !== id));
  };

  const handleSubmit = async (values: OfferFormValues) => {
    if (panel.length === 0) {
      toast({ title: t('INTERVIEW_PANEL_REQUIRED'), variant: 'destructive' });
      return;
    }
    // HiringDepartmentName is a denormalized display field, resolved client-side from the
    // already-fetched departments list rather than relying on server-side stamping.
    const hiringDepartmentName =
      departments.find((department) => department.ItemId === values.HiringDepartmentId)?.Title ??
      '';
    try {
      await createConditionalOffer({
        ...values,
        HiringDepartmentName: hiringDepartmentName,
        InterviewPanelJson: JSON.stringify(panel),
      });
      navigate('/hr/conditional-offers');
    } catch {
      // Error toast is already shown by the mutation's global error handler.
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="flex w-full flex-col gap-5">
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
          <CandidateFields />
          <FormField
            control={form.control}
            name="HiringDepartmentId"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('DEPARTMENT')}</FormLabel>
                <Select onValueChange={field.onChange} value={field.value}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder={t('DEPARTMENT')} />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {departments.map((department) => (
                      <SelectItem key={department.ItemId} value={department.ItemId}>
                        {department.Title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="space-y-2">
          <Label>{t('INTERVIEW_PANEL')}</Label>
          <div className="flex items-end gap-2">
            <div className="flex-1">
              <Select value={selectedEmployeeId} onValueChange={setSelectedEmployeeId}>
                <SelectTrigger>
                  <SelectValue placeholder={t('EMPLOYEE')} />
                </SelectTrigger>
                <SelectContent>
                  {employees.map((employee) => (
                    <SelectItem key={employee.ItemId} value={employee.ItemId}>
                      {employee.DisplayName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button
              type="button"
              variant="outline"
              onClick={addPanelMember}
              disabled={!selectedEmployeeId}
            >
              {t('ADD')}
            </Button>
          </div>
          {panel.map((member) => (
            <div
              key={member.id}
              className="flex items-center justify-between rounded-md border px-3 py-2 text-sm"
            >
              <span>{member.name}</span>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => removePanelMember(member.id)}
              >
                <Trash className="size-4" />
              </Button>
            </div>
          ))}
        </div>

        <FormField
          control={form.control}
          name="Comment"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('COMMENT')}</FormLabel>
              <FormControl>
                <Textarea rows={3} placeholder={t('COMMENT')} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="flex justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/hr/conditional-offers')}
          >
            {t('CANCEL')}
          </Button>
          <Button type="submit" loading={isPending} disabled={isPending}>
            {t('CREATE')}
          </Button>
        </div>
      </form>
    </Form>
  );
};
